import { forEach } from 'ramda'
import { Userpilot } from 'userpilot'
import { parseErrorMessage } from '@/ports/api'
import { toast } from '@/components/toast-notifications'
import queryClient from '@/components/query-client'
import { pushToDatalayer } from '@/utils/push-datalayer'
import {
  login,
  requestAutoLogin,
  setInitialPassword,
  logout,
  autoLogin,
  externalAutoLogin,
  tokenSession,
  tokenSessionValidate
} from './ports'

const setGaUserId = userId => pushToDatalayer({ userId })

const auth = {
  state: {},
  reducers: {
    update: (state, payload) => ({ ...state, ...payload })
  },
  effects: dispatch => ({
    async logout() {
      try {
        await logout()
        await dispatch.session.clear()

        const localStorageKeysToRemove = ['session-id', '@session:hasLoggedBefore']
        forEach(item => localStorage.removeItem(item), localStorageKeysToRemove)
        queryClient.clear()
        Userpilot?.destroy()
      } catch (error) {
        console.error('[auth/logout] Error Occured: ', error)
      }
    },
    async setInitialPassword(payload) {
      try {
        const { response, body } = await setInitialPassword({ body: payload })
        if (!response.ok) {
          throw new Error(body.message || response.statusText)
        }
        await dispatch.session.loadSession()
        dispatch.master.setReady()
        return { response, body }
      } catch (error) {
        if (error?.data?.password_setup_required) {
          throw error
        }
        return { error }
      }
    },
    async login(payload) {
      try {
        const { response, body } = await login({ body: payload })
        if (!response.ok) {
          const message = parseErrorMessage(body)
          if (
            message === 'Invalid login info.' ||
            message ===
              'The string should match pattern: ^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@+.#?*$%^&)(>/<_\\\\.\\-|\\]\\[])(?=.{8,})'
          ) {
            return toast('The email or password you entered is invalid. Please try again.', {
              type: 'error',
              title: 'Login Failed'
            })
          }
          toast(message, {
            type: 'error',
            title: 'Login Failed'
          })
          throw new Error(body.message || response.statusText)
        }
        await dispatch.session.loadSession({ eventId: 'web-login' })
        return { response, body }
      } catch (error) {
        if (error?.data?.password_setup_required) {
          throw error
        }
        return { error }
      }
    },
    async requestAutoLogin(payload) {
      try {
        const { response, body } = await requestAutoLogin(payload)
        if (!response.ok) {
          throw new Error(body.message || response.statusText)
        }
        const email = body?.email || payload.username
        return { response, body, email }
      } catch (error) {
        return { error }
      }
    },
    async autoLogin(loginHash) {
      try {
        const { response } = await autoLogin(undefined, { loginHash })
        if (response.ok) {
          localStorage.setItem('auto-login', true)
        }
      } catch (error) {
        console.error('[auth/validatePhone] Error Occured: ', error)
      }
    },
    async externalAutoLogin(loginHash) {
      try {
        const { response, body } = await externalAutoLogin({ loginHash })

        if (response.ok) {
          localStorage.setItem('auto-login', true)
          localStorage.setItem('session-id', body?.signer?.id)
          setGaUserId(body?.signer?.id)
          dispatch.session.set({ session: { ...body, registration_complete: '1' } })
        }
      } catch (error) {
        console.log('error', error)
        console.error('[auth/validatePhone] Error Occured: ', error)
      }
    },
    async getSessionWithToken(token) {
      try {
        const { response, body } = await tokenSession({ body: { auth_token: token } })

        await dispatch.session.loadSession({ eventId: 'token-web-login' })

        return { tokenSessionResponse: response, tokenSessionBody: body }
      } catch (error) {
        console.error('[auth/getSessionWithToken] Error Occured: ', error)
      }
    },
    async tokenSessionValidate(token) {
      try {
        const { response, body } = await tokenSessionValidate({ body: { auth_token: token } })
        return { response, body }
      } catch (error) {
        console.error('[auth/tokenSessionValidate] Error Occured: ', error)
      }
    }
  })
}

export default auth
